// src/components/Inventory/LocationPicker.js
// Selector de ubicación de LISTAS PREDEFINIDAS (§4b): el obrero elige, nunca
// escribe texto libre (evita "Rack B4"/"rack b-4"/"B 4"). Tres modos:
// paleta (license plate) · rack fijo · zona de piso.
import React from 'react';
import { t } from '../../lib/i18n';
import { staticLocationLabel } from '../../lib/locations';

const TYPES = [
    { key: 'pallet', label: () => t('location.type.pallet') },
    { key: 'rack', label: () => t('location.type.rack') },
    { key: 'zone', label: () => t('location.type.zone') },
];

/**
 * @param {object} props
 * @param {object} props.cfg      { warehouses, racks, zones, pallets } de loadLocationConfig
 * @param {object|null} props.value   ubicación { type, ... } o null
 * @param {Function} props.onChange   recibe la ubicación normalizada (con label) o null
 * @param {boolean} [props.fixedOnly] solo rack/zona (para crear/mover paletas)
 */
const LocationPicker = ({ cfg, value, onChange, fixedOnly = false }) => {
    const types = fixedOnly ? TYPES.filter((x) => x.key !== 'pallet') : TYPES;
    const hasPallets = (cfg?.pallets || []).some((p) => p.status !== 'retired');
    // Default usable: si no hay paletas creadas, arranca en "Zona" (no en un dropdown
    // de paletas vacío que deja al obrero sin opción).
    const type = value?.type || (fixedOnly ? 'zone' : (hasPallets ? 'pallet' : 'zone'));
    const warehouseId = value?.warehouseId || cfg?.warehouses?.[0]?.id || '';

    const hasConfig = (cfg?.warehouses?.length || 0) > 0 || (cfg?.pallets?.length || 0) > 0;
    if (!hasConfig) {
        return <p className="text-sm text-amber-700 bg-amber-50 rounded-md p-3">{t('location.noneConfigured')}</p>;
    }

    const setType = (nextType) => onChange({ type: nextType, warehouseId });

    const emit = (loc) => {
        // El label denormalizado viaja con la ubicación (cosmético; la ref es lo canónico).
        if (loc.type === 'pallet') {
            const p = (cfg.pallets || []).find((x) => x.id === loc.palletId);
            onChange({ ...loc, label: p?.code || loc.palletId });
        } else {
            onChange({ ...loc, label: staticLocationLabel(loc, cfg) });
        }
    };

    const racksOfWh = (cfg.racks || []).filter((r) => !r.warehouseId || r.warehouseId === warehouseId);
    const zonesOfWh = (cfg.zones || []).filter((z) => !z.warehouseId || z.warehouseId === warehouseId);
    const activePallets = (cfg.pallets || []).filter((p) => p.status !== 'retired');

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('location.label')}</label>
            <div className="flex gap-2">
                {types.map(({ key, label }) => (
                    <button key={key} type="button" onClick={() => setType(key)}
                        className={`px-3 py-2 text-sm rounded-md border ${type === key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                        {label()}
                    </button>
                ))}
            </div>

            {type === 'pallet' && (
                activePallets.length === 0 ? (
                    <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2">{t('location.noPallets')}</p>
                ) : (
                    <select className="w-full p-2 border border-gray-300 rounded-md"
                        value={value?.palletId || ''}
                        onChange={(e) => emit({ type: 'pallet', palletId: e.target.value })}>
                        <option value="">{t('location.pick.pallet')}</option>
                        {activePallets.map((p) => (
                            <option key={p.id} value={p.id}>{p.code || p.id} ({staticLocationLabel(p.currentLocation, cfg) || '—'})</option>
                        ))}
                    </select>
                )
            )}

            {type !== 'pallet' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <select className="w-full p-2 border border-gray-300 rounded-md" value={warehouseId}
                        onChange={(e) => onChange({ type, warehouseId: e.target.value })}>
                        {(cfg.warehouses || []).map((w) => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
                    </select>
                    {type === 'rack' ? (
                        <select className="w-full p-2 border border-gray-300 rounded-md" value={value?.rackId || ''}
                            onChange={(e) => emit({ type: 'rack', warehouseId, rackId: e.target.value })}>
                            <option value="">{t('location.pick.rack')}</option>
                            {racksOfWh.map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
                        </select>
                    ) : (
                        <select className="w-full p-2 border border-gray-300 rounded-md" value={value?.zoneId || ''}
                            onChange={(e) => emit({ type: 'zone', warehouseId, zoneId: e.target.value })}>
                            <option value="">{t('location.pick.zone')}</option>
                            {zonesOfWh.map((z) => <option key={z.id} value={z.id}>{z.name || z.id}</option>)}
                        </select>
                    )}
                </div>
            )}
        </div>
    );
};

/** ¿La ubicación está completa para mandarla al servidor? PURO. */
export function isLocationComplete(loc) {
    if (!loc || !loc.type) return false;
    if (loc.type === 'pallet') return !!loc.palletId;
    if (loc.type === 'rack') return !!(loc.warehouseId && loc.rackId);
    if (loc.type === 'zone') return !!(loc.warehouseId && loc.zoneId);
    return false;
}

export default LocationPicker;
